/**
 * RFIsList Component
 * Main container for RFI management with React Query.
 * Follows PermitsList pattern (Gold Standard).
 *
 * - Hooks in src/hooks/useRFIs.ts (not inline mutations)
 * - Proper cache invalidation with prefix matching
 * - RFIDetailPanel for full detail + file attachments
 *
 * @see docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 */
import { useState } from 'react';
import { RFI } from '@/types/rfis';
import { RFICard } from './RFICard';
import { AddRFIModal } from './AddRFIModal';
import { RFIDetailPanel } from './RFIDetailPanel';
import { Button } from '@/components/ui/button';
import { Plus, MessageSquare } from 'lucide-react';
import { useRFIs, useDeleteRFI } from '@/hooks';
import { EmptyState } from '@/components/ui/empty-state';

interface RFIsListProps {
  projectId: string;
  hideHeader?: boolean;
}

export function RFIsList({ projectId, hideHeader = false }: RFIsListProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRFI, setEditingRFI] = useState<RFI | null>(null);
  const [selectedRFIId, setSelectedRFIId] = useState<string | null>(null);

  const { data, isLoading } = useRFIs(projectId);
  const rfis = data?.rfis ?? [];
  const deleteRFI = useDeleteRFI();

  const handleCardClick = (rfi: RFI) => {
    setSelectedRFIId(rfi.id);
  };

  const handleEdit = (rfi: RFI) => {
    setSelectedRFIId(null);
    setEditingRFI(rfi);
    setIsModalOpen(true);
  };

  const handleDelete = async (rfiId: string) => {
    if (confirm('Are you sure you want to delete this RFI?')) {
      await deleteRFI.mutateAsync(rfiId);
    }
  };

  const handleAddNew = () => {
    setEditingRFI(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRFI(null);
  };

  if (isLoading) {
    return <div>Loading RFIs...</div>;
  }

  // Issue #96: When the empty state renders its own "Create RFI" CTA, suppress
  // the duplicate header "Add RFI" button. Header still shows the title row when
  // hideHeader=false (so empty users keep one heading + one CTA, not two CTAs).
  const isEmpty = rfis.length === 0;

  return (
    <div className="space-y-4">
      {!hideHeader ? (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">RFIs ({rfis.length})</h3>
          {!isEmpty && (
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add RFI
            </Button>
          )}
        </div>
      ) : (
        !isEmpty && (
          <div className="flex justify-end">
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add RFI
            </Button>
          </div>
        )
      )}

      {isEmpty ? (
        <EmptyState
          icon={MessageSquare}
          title="No RFIs yet"
          description="Track requests for information between your team and consultants"
          actionLabel="Create RFI"
          onAction={handleAddNew}
        />
      ) : (
        <div className="space-y-2">
          {rfis.map((rfi) => (
            <RFICard
              key={rfi.id}
              rfi={rfi}
              onClick={handleCardClick}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AddRFIModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        projectId={projectId}
        editingRFI={editingRFI}
      />

      <RFIDetailPanel
        rfiId={selectedRFIId}
        onClose={() => setSelectedRFIId(null)}
        onEdit={handleEdit}
      />
    </div>
  );
}
